import React, { useContext, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Link as MuiLink,
} from '@mui/material';
import { useQuery } from '@apollo/client';
import { useNavigate, useParams } from 'react-router-dom';
import { UserContext } from '../auth/auth-context';
import { GET_WALLET_SUMMARY} from '../../graphql/queries/tip';
import { MIN_COINS_TO_WITHDRAW } from '../../util/constants';
import { rootBkndPath } from '../../util/link'; 
import PaymentSettings from '../../pages/customer/booking/paymentSettings';
import { GET_PAYMENTS } from '../../graphql/queries';

const COIN_VALUE_USD = 0.10;
const PLATFORM_FEE_RATE = 0.10;
const PAYPAL_FEE_RATE = 0.029;
const PAYPAL_FLAT_FEE_USD = 0.30;
const NET_FEE_MULTIPLIER = 1 - (PLATFORM_FEE_RATE + PAYPAL_FEE_RATE);

function WithdrawButton({ amount, onSuccess, onError, disabled }) {
  const [loading, setLoading] = useState(false);

  const handleClick = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        onError('Authentication required. Please log in.');
        setLoading(false);
        return;
      }

      const res = await fetch(`${rootBkndPath}/api/payout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        credentials: 'include',
        body: JSON.stringify({ amount }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Payout failed');
      onSuccess(json);
    } catch (err) {
      onError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button variant="contained" onClick={handleClick} disabled={disabled || loading}>
      {loading ? <CircularProgress size={20} /> : 'Withdraw'}
    </Button>
  );
}

export default function WithdrawPlanyCoins() {
  const { businessUrl, staffUrl } = useParams();
  const { providerUser } = useContext(UserContext);
  const navigate = useNavigate();

  const [amount, setAmount] = useState(MIN_COINS_TO_WITHDRAW);
  const [errorMsg, setErrorMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [successOpen, setSuccessOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);

  // Fetch wallet summary and payment methods
  const { data, loading, error } = useQuery(GET_WALLET_SUMMARY, {
    variables: { userId: providerUser?.userId },
    skip: !providerUser?.userId,
  });
  const {
    data: paymentsData,
    loading: paymentsLoading,
    error: paymentsError
  } = useQuery(GET_PAYMENTS, {
    variables: { businessUrl: businessUrl },
    skip: !businessUrl,
  });

  if (loading || paymentsLoading) return <CircularProgress />;
  if (error) return <Alert severity="error">Error loading wallet.</Alert>;
  if (paymentsError) return <Alert severity="error">Error loading payment methods.</Alert>;

  const balance = data.getWalletSummary.balance;
  const grossUsd = amount * COIN_VALUE_USD;
  const netUsd = grossUsd * NET_FEE_MULTIPLIER - PAYPAL_FLAT_FEE_USD;

  // Check if PayPal method exists
  const hasPayPal = paymentsData.payments.some(
    p => p.paymentMethod.type.toLowerCase() === 'paypal'
  );

  const handleSuccess = (resp) => {
    setSuccessMsg(resp.message);
    setSuccessOpen(true);
  };

  const handleError = (msg) => setErrorMsg(msg);

  const handleSuccessOk = () => {
    setSuccessOpen(false);
    navigate(`/${businessUrl}/${staffUrl}/wallet`, { state: { didWithdraw: true } });
  };

  const handleSettingsOpen = () => setSettingsOpen(true);
  const handleSettingsClose = () => setSettingsOpen(false);

  // disable if insufficient
  const disableWithdraw =
    amount < MIN_COINS_TO_WITHDRAW ||
    amount > balance ||
    netUsd <= 0;

  return (
    <>
      {/* Withdraw Form */}
      <Dialog open fullWidth maxWidth="sm">
        <DialogTitle>Withdraw PlanyCoins</DialogTitle>
        <DialogContent>
          <Box mb={2}>
            <Typography>Balance: {balance} PlanyCoins</Typography>
            <TextField
              fullWidth
              type="number"
              label="Coins to Withdraw"
              InputProps={{ inputProps: { min: MIN_COINS_TO_WITHDRAW, max: balance } }}
              value={amount}
              onChange={e => setAmount(Number(e.target.value))}
              helperText={`Minimum ${MIN_COINS_TO_WITHDRAW} coins`}
              sx={{ mt: 1 }}
            />
            <Typography sx={{ mt: 2 }}>
              Net USD after fees: ${netUsd.toFixed(2)}
            </Typography>
          </Box>

          {errorMsg && <Alert severity="error" sx={{ mb: 2 }}>{errorMsg}</Alert>}

          {!hasPayPal ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              No PayPal payment method set.
              <Button variant="text" onClick={handleSettingsOpen} sx={{ ml: 1 }}>
                Configure now
              </Button>
            </Alert>
          ) : (
            <Box display="flex" justifyContent="flex-end">
              <WithdrawButton
                amount={amount}
                onSuccess={handleSuccess}
                onError={handleError}
                disabled={disableWithdraw}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => navigate(`/${businessUrl}/${staffUrl}/wallet`)}>
            Back to Wallet
          </Button>
        </DialogActions>
      </Dialog>

      {/* Settings Dialog */}
      <Dialog open={settingsOpen} onClose={handleSettingsClose} fullWidth maxWidth="md">
        <DialogTitle>Business Integration Settings</DialogTitle>
        <DialogContent dividers>
          <PaymentSettings isDisabled={false} />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSettingsClose}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Success Confirmation */}
      <Dialog open={successOpen} onClose={() => {}} maxWidth="xs" fullWidth>
        <DialogTitle>Withdrawal Successful</DialogTitle>
        <DialogContent>
          <Typography>{successMsg}</Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={handleSuccessOk}>
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
